import React, { useState, useEffect, useMemo } from 'react';
import { Card, Mask, Calendar } from 'antd-mobile';
import ReactApexChart from 'react-apexcharts';
import { useStore } from '../store/useStore';
import { theme } from '../theme';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://porddee-api.lungnuek.workers.dev/api';

const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const toBangkokDateStr = (date: Date) => date.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
const bangkokStartOfDay = (date: Date) => new Date(toBangkokDateStr(date) + 'T00:00:00+07:00');
const bangkokEndOfDay = (date: Date) => new Date(toBangkokDateStr(date) + 'T23:59:59.999+07:00');

const isSameDay = (a: Date, b: Date) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

const daysBetween = (a: Date, b: Date) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const aDate = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bDate = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((bDate.getTime() - aDate.getTime()) / msPerDay);
};

export const Statistics: React.FC = () => {
  const { user, todayCount: storeTodayCount } = useStore();
  const [rangeStart, setRangeStart] = useState(() => new Date());
  const [rangeEnd, setRangeEnd] = useState(() => new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [tempRangeStart, setTempRangeStart] = useState<Date | null>(null);
  const [tempRangeEnd, setTempRangeEnd] = useState<Date | null>(null);

  useEffect(() => { document.title = 'สถิติ'; return () => { document.title = 'porddee - ตัวช่วยเลิกสูบ'; }; }, []);

  const [dailyTotal, setDailyTotal] = useState(0);
  const [chartData, setChartData] = useState<{ x: string, y: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isRangeSingleDay = isSameDay(rangeStart, rangeEnd);
  const rangeLength = daysBetween(rangeStart, rangeEnd) + 1;

  const formatDate = (date: Date) => `${date.getDate()} ${THAI_MONTHS_SHORT[date.getMonth()]} ${date.getFullYear() + 543}`;

  const formatRangeDisplay = () => {
    if (isRangeSingleDay) return formatDate(rangeStart);
    return `${formatDate(rangeStart)} - ${formatDate(rangeEnd)}`;
  };

  useEffect(() => {
    if (!user?.id) return;
    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const start = bangkokStartOfDay(rangeStart);
        const end = bangkokEndOfDay(rangeEnd);
        const response = await fetch(`${API_BASE_URL}/logs/${user.id}?startDate=${start.toISOString()}&limit=10000`);
        if (response.ok) {
          const data = await response.json();
          const logs = data.logs || [];
          const filteredLogs = logs.filter((log: any) => {
            const logDate = new Date(log.loggedAt);
            return logDate >= start && logDate <= end;
          });

          setDailyTotal(filteredLogs.reduce((sum: number, log: any) => sum + log.count, 0));

          if (isRangeSingleDay) {
            const hourlyData = Array(24).fill(0).map((_, i) => ({ x: `${i}:00`, y: 0 }));
            filteredLogs.forEach((log: any) => {
              const h = parseInt(new Date(log.loggedAt).toLocaleTimeString('en-US', { timeZone: 'Asia/Bangkok', hour12: false, hour: '2-digit' }));
              if (h >= 0 && h < 24) hourlyData[h].y += log.count;
            });
            setChartData(hourlyData);
          } else {
            const dailyData: { x: string; y: number }[] = [];
            for (let i = 0; i < rangeLength; i++) {
              const d = new Date(rangeStart);
              d.setDate(d.getDate() + i);
              dailyData.push({ x: `${d.getDate()}/${d.getMonth() + 1}`, y: 0 });
            }
            filteredLogs.forEach((log: any) => {
              const logBangkok = toBangkokDateStr(new Date(log.loggedAt));
              const startBangkok = toBangkokDateStr(rangeStart);
              const dayIndex = Math.round((new Date(logBangkok + 'T00:00:00').getTime() - new Date(startBangkok + 'T00:00:00').getTime()) / (1000 * 60 * 60 * 24));
              if (dayIndex >= 0 && dayIndex < rangeLength) dailyData[dayIndex].y += log.count;
            });
            setChartData(dailyData);
          }
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLogs();
  }, [rangeStart, rangeEnd, user?.id, storeTodayCount, isRangeSingleDay, rangeLength]);

  const dailyLimit = user?.dailyPuffLimit ?? 20;
  const totalLimit = dailyLimit * rangeLength;
  const nicotinePerPuff = user?.nicotinePerPuff || 0.1;
  const totalNicotine = +(dailyTotal * nicotinePerPuff).toFixed(2);
  const overLimit = Math.max(0, dailyTotal - totalLimit);

  const series = [{ name: 'จำนวนสูบ', data: chartData.map(d => d.y) }];

  const options: any = useMemo(() => {
    const isSingleDay = chartData.length === 24;
    return {
      chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: '"Prompt", sans-serif' },
      plotOptions: {
        bar: {
          borderRadius: isSingleDay ? 2 : (rangeLength > 14 ? 2 : 4),
          columnWidth: isSingleDay || rangeLength > 14 ? '80%' : '60%',
          dataLabels: { position: 'top' }
        }
      },
      dataLabels: {
        enabled: rangeLength <= 14, // ซ่อนตัวเลขบนแท่งกราฟถ้าเลือกดูเกิน 14 วัน
        formatter: (val: number) => val > 0 ? val : '',
        style: { fontSize: '10px', colors: ['#64748b'] },
        offsetY: -20
      },
      xaxis: {
        categories: chartData.map(d => d.x),
        labels: {
          show: true,
          style: { fontSize: '10px', colors: '#94a3b8' },
          hideOverlappingLabels: true, // ซ่อนตัวหนังสือที่ทับซ้อนกัน
          rotate: -45, // เอียงตัวหนังสือเมื่อที่แคบ
          rotateAlways: false,
        },
        axisBorder: { show: false }, axisTicks: { show: false },
        tickAmount: isSingleDay ? 6 : (rangeLength > 14 ? Math.min(rangeLength, 10) : undefined)
      },
      yaxis: { show: false, max: (max: number) => Math.max(max, isSingleDay ? Math.max(dailyLimit / 2, 5) : dailyLimit) + 2 },
      grid: { show: false },
      colors: [theme.primary],
      fill: {
        type: 'gradient',
        gradient: { shade: 'light', type: 'vertical', shadeIntensity: 0.5, gradientToColors: [theme.primaryLight], inverseColors: true, opacityFrom: 1, opacityTo: 1, stops: [0, 100] }
      },
      annotations: {
        yaxis: isSingleDay ? undefined : [{
          y: dailyLimit,
          borderColor: '#ef4444',
          strokeDashArray: 5,
          label: {
            borderColor: '#ef4444',
            style: { color: '#fff', background: '#ef4444', padding: { left: 8, right: 8, top: 4, bottom: 4 } },
            text: 'เป้าหมาย'
          }
        }]
      }
    };
  }, [chartData, dailyLimit, rangeLength]);

  const generateInsight = () => {
    if (dailyTotal === 0) return '🎉 สบายมาก ยังไม่ได้สูบเลยในรอบนี้! รักษาความดีนี้ไว้นะครับ';
    if (overLimit > 0) return '⚠️ แอบเกินเป้ามานิดนึง ไม่เป็นไร ถือว่าเป็นการเรียนรู้ ลองหาอะไรทำเพลินๆ ตอนอยากสูบนะครับ';
    if (dailyTotal <= totalLimit) return '👍 คุมจำนวนสูบได้เยี่ยมมาก! สอดคล้องกับแผนที่วางไว้เลย';
    return 'กำลังดีเลย ทำต่อไปนะ';
  };

  const maxPeak = chartData.length > 0 ? Math.max(...chartData.map(d => d.y)) : 0;
  const peakTime = chartData.find(d => d.y === maxPeak)?.x;

  return (
    <div className="page" style={{ background: '#f8fafc', minHeight: '100dvh', paddingBottom: 100 }}>
      {/* HEADER */}
      <div style={{ padding: '24px 24px 12px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>

        <button
          onClick={() => {
            setTempRangeStart(rangeStart);
            setTempRangeEnd(rangeEnd);
            setCalendarOpen(true);
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 20,
            background: 'white', border: '1px solid #e2e8f0',
            color: '#0f172a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}
        >
          📅 {formatRangeDisplay()} ▾
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        <Card style={{ borderRadius: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.04)', marginBottom: 16, padding: '20px 16px', background: '#fff', border: 'none' }}>
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: '#64748b', fontWeight: 600 }}>ปริมาณทั้งหมด ({isRangeSingleDay ? 'รายวัน' : 'รวมทั้งหมด'})</div>
            <div style={{ fontSize: 44, fontWeight: 900, color: theme.primary, lineHeight: 1.1 }}>{dailyTotal}</div>
          </div>

          {/* CHART */}
          <div style={{ height: 240, position: 'relative' }}>
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>⏳ กำลังโหลดข้อมูล...</div>
            ) : dailyTotal > 0 ? (
              <ReactApexChart options={options} series={series} type="bar" height={240} width="100%" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                <span style={{ fontSize: 40, marginBottom: 8 }}>📊</span>
                ไม่มีข้อมูลการสูบ
              </div>
            )}
          </div>
        </Card>

        {/* AI INSIGHT */}
        <div style={{
          background: overLimit > 0 ? 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)' : 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
          borderRadius: 20, padding: 16, marginBottom: 16,
          border: overLimit > 0 ? '1px solid #fecdd3' : '1px solid #d1fae5',
          boxShadow: overLimit > 0 ? '0 4px 12px rgba(225, 29, 72, 0.05)' : '0 4px 12px rgba(16, 185, 129, 0.05)'
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 24 }}>💡</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: overLimit > 0 ? '#be123c' : '#065f46', marginBottom: 4 }}>Porddee Insight</div>
              <div style={{ fontSize: 13, color: overLimit > 0 ? '#9f1239' : '#047857', lineHeight: 1.5, fontWeight: 500 }}>
                {generateInsight()} {maxPeak > 0 && isRangeSingleDay ? ` วันนี้คุณสูบถี่ที่สุดในช่วงเวลาประมาณ ${peakTime} น. ลองสังเกตตัวเองดูนะว่าช่วงเวลานี้ทำอะไรอยู่` : ''}
              </div>
            </div>
          </div>
        </div>

        {/* STATS PANEL */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: 'white', borderRadius: 20, padding: 16, textAlign: 'center', border: '1px solid #f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b', marginBottom: 4 }}>{totalNicotine} <span style={{ fontSize: 12 }}>mg</span></div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>นิโคตินที่รับ</div>
          </div>
          <div style={{ flex: 1, background: 'white', borderRadius: 20, padding: 16, textAlign: 'center', border: '1px solid #f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: overLimit > 0 ? '#ef4444' : '#10b981', marginBottom: 4 }}>
              {overLimit}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{overLimit > 0 ? 'จำนวนที่เกินเป้า' : 'อยู่ในโควต้า'}</div>
          </div>
        </div>
      </div>

      <Mask visible={calendarOpen} onMaskClick={() => setCalendarOpen(false)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', padding: '0 20px' }}>
          <div style={{ background: 'white', borderRadius: 24, padding: '20px', width: '100%', maxWidth: 360, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            {/* Calendar Area */}
            <div style={{
              '--adm-color-primary': theme.primary,
              '--adm-color-text': '#111827',
              '--adm-calendar-header-font-size': '16px',
              '--adm-calendar-title-font-size': '16px',
              '--adm-calendar-day-font-size': '15px',
            } as any}>
              <Calendar
                key={`${tempRangeStart?.getTime()}-${tempRangeEnd?.getTime()}`}
                selectionMode="range"
                defaultValue={[tempRangeStart || new Date(), tempRangeEnd || new Date()]}
                onChange={(val: any) => {
                  if (Array.isArray(val)) {
                    setTempRangeStart(val[0]);
                    setTempRangeEnd(val[1] || val[0]);
                  } else if (val) {
                    setTempRangeStart(val);
                    setTempRangeEnd(val);
                  }
                }}
              />
            </div>

            <div style={{ height: 20 }} />

            {/* Quick Select Buttons */}
            {(() => {
              const tLen = tempRangeStart && tempRangeEnd ? daysBetween(tempRangeStart, tempRangeEnd) + 1 : 0;
              const tEndToday = tempRangeEnd ? isSameDay(tempRangeEnd, new Date()) : false;
              const isT1 = tLen === 1 && tEndToday;
              const isT7 = tLen === 7 && tEndToday;
              const isT30 = tLen === 30 && tEndToday;
              return (
                <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                  <button
                    onClick={() => { const d = new Date(); setTempRangeStart(d); setTempRangeEnd(d); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 20, border: isT1 ? `1px solid ${theme.primary}` : '1px solid #e5e7eb', background: isT1 ? theme.primary : 'white', color: isT1 ? 'white' : '#4b5563', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    วันนี้
                  </button>
                  <button
                    onClick={() => { const d = new Date(); d.setDate(d.getDate() - 6); setTempRangeStart(d); setTempRangeEnd(new Date()); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 20, border: isT7 ? `1px solid ${theme.primary}` : '1px solid #e5e7eb', background: isT7 ? theme.primary : 'white', color: isT7 ? 'white' : '#4b5563', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    7 วัน
                  </button>
                  <button
                    onClick={() => { const d = new Date(); d.setDate(d.getDate() - 29); setTempRangeStart(d); setTempRangeEnd(new Date()); }}
                    style={{ flex: 1, padding: '10px 0', borderRadius: 20, border: isT30 ? `1px solid ${theme.primary}` : '1px solid #e5e7eb', background: isT30 ? theme.primary : 'white', color: isT30 ? 'white' : '#4b5563', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    30 วัน
                  </button>
                </div>
              );
            })()}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => setCalendarOpen(false)}
                style={{ flex: 1, padding: '14px 0', borderRadius: 24, border: 'none', background: '#f3f4f6', color: '#4b5563', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
                ยกเลิก
              </button>
              <button
                onClick={() => {
                  if (tempRangeStart && tempRangeEnd) {
                    setRangeStart(tempRangeStart);
                    setRangeEnd(tempRangeEnd);
                  }
                  setCalendarOpen(false);
                }}
                style={{ flex: 1, padding: '14px 0', borderRadius: 24, border: 'none', background: theme.primary, color: 'white', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
                ตกลง
              </button>
            </div>
          </div>
        </div>
      </Mask>
    </div>
  );
};
